import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import TickerTimeout from "../../../utils/TickerTimeout";
import {
  setShowPotCollectionMaxWin,
  setPotCollectionOutroTransitionActive,
  setPotCollectionOutroCounterFinished,
} from "../../../State/actions";
import { Settings } from "../../../settings";
import { PixiSettings } from "../../../pixiSettings";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import { HowlCache } from "../../../State/Slices/soundsSlice";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { formatToCustomLocale } from "../../../utilities";
import FXManager from "../../../Engine/Effects/FXManager";

// Device and orientation-specific config (same as PotCollectionOutroDialogController)
const DEVICE_MULTIPLIERS = {
  MOBILE_SMALL_PORTRAIT: { scale: 1.0, offset: 0, buttonYOffset: 1.25 },
  MOBILE_PORTRAIT: { scale: 1.0, offset: 0, buttonYOffset: 2.75 },
  MOBILE_LANDSCAPE: { scale: 0.8, offset: 0, buttonYOffset: 1.0 }, 
  TABLET_PORTRAIT: { scale: 1.4, offset: 0, buttonYOffset: 0 },
  TABLET_LANDSCAPE: { scale: 1.3, offset: 0, buttonYOffset: 0 },
  DESKTOP: { scale: 1.0, offset: 0, buttonYOffset: 0 },
};

/**
 * PotCollectionMaxWinDialogController
 *
 * Max Win (10,000x) popup for the Pot Collection bonus game — GDD §18.
 * Uses the win_celebration spine with the "max_win" skin.
 *
 * Key differences from PotCollectionOutroDialogController:
 *   - Uses "max_win" skin instead of "total_win"
 *   - NO auto-close timer (GDD §16.4: "Until user clicks", explicit only)
 *   - Same transition on dismiss: setPotCollectionOutroTransitionActive(true)
 *
 * Flow: setSkin("max_win") → in → idle → user click (explicit only) → out → transition
 */
class PotCollectionMaxWinDialogController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.t = t;

    this.dimensions = dimensionsContext.dimensions;

    this.outroSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;
    this.hasHandledCloseClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;
    this.inIdleTimeout = null;

    this._totalWinValue = 0;
    this._betAmount = 0;

    this.counterContainer = null;
    this.counterText = null;
    this._counterBaseStyle = null;

    this.multiplierContainer = null;
    this.multiplierText = null;

    this.buttonContainer = null;
    this.buttonText = null;

    // Click handler (set by component via setButtonClickHandler)
    this._buttonClickHandler = null;

    this._deviceMultipliersCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeOutroSpine();
  }

  // ===================================================================
  //  CONFIG / SCALING
  // ===================================================================

  setButtonClickHandler(handler) {
    this._buttonClickHandler = handler;
  }

  getDeviceMultipliers() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext;

    if (
      this._deviceMultipliersCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._deviceMultipliersCache;
    }

    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = isMobileSmall
          ? DEVICE_MULTIPLIERS.MOBILE_SMALL_PORTRAIT
          : DEVICE_MULTIPLIERS.MOBILE_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS.MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = DEVICE_MULTIPLIERS.TABLET_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS.TABLET_LANDSCAPE;
      }
    } else {
      multipliers = DEVICE_MULTIPLIERS.DESKTOP;
    }

    this._deviceMultipliersCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    this._lastIsMobileSmall = isMobileSmall;
    return multipliers;
  }

  applyScaleAndCenter() {
    if (!this.outroSpine || !this.dimensions) return;

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.outroSpine.scale.set(scale);

    const baseOffset = this.symbolHeight || 0;
    this.outroSpine.x = 0;
    this.outroSpine.y = -baseOffset * deviceOffsetMultiplier;

    // Keep coin shower proportional to spine on resize
    if (this._coinShowerContainer && !this._coinShowerContainer.destroyed) {
      this._coinShowerContainer.scale.set(scale * 2.5);
    }
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;

    if (!dimensionsChanged && !symbolHeightChanged) return;

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;

    if (
      oldDevice !== newDevice ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation ||
      this._lastIsMobileSmall !== dimensionsContext.isMobileSmall
    ) {
      this._deviceMultipliersCache = null;
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    if (
      this.outroSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
        this.applyButtonOffset();
      }
    }
  }

  // ===================================================================
  //  SPINE CREATION
  // ===================================================================

  initializeOutroSpine() {
    try {
      this.outroSpine = this.createOutroSpine();

      if (!this.outroSpine) return;
      if (!this.outroSpine.skeleton || !this.outroSpine.state) return;
      if (!this.outroSpine.skeleton.data) return;

      this.container.addChild(this.outroSpine);
      this.outroSpine.alpha = 0.001;
      this.outroSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.outroSpine.parent !== this.container) return;

      this.initialized = true;
      this._availableAnimationsCache = null;

      const stateData = this.outroSpine.state.data;
      stateData.setMix("in", "idle", 0.15);
      stateData.setMix("idle", "out", 0.25);

      this.warmUpOutroSpine();
      this.initializeCounter();
      this.initializeMultiplier();
      this.initializeButton();

      SpineAtlasUploader.upload("win_celebration");
    } catch {
      // Silent fail
    }
  }

  createOutroSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "win_celebration-skeleton";
      const atlasAlias = "win_celebration-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

      const outroSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!outroSpine?.skeleton?.data?.animations) return null;

      return outroSpine;
    } catch {
      return null;
    }
  }

  warmUpOutroSpine() {
    if (!this.outroSpine?.state) return;
    try {
      this.applySkin();

      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.outroSpine.state.setAnimation(0, animations[0], false);
        this.outroSpine.state.update(0.016);
        this.outroSpine.skeleton.updateWorldTransform();
      }
      this.outroSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  getAvailableAnimations() {
    if (!this.outroSpine?.skeleton?.data?.animations) return [];
    if (this._availableAnimationsCache !== null)
      return this._availableAnimationsCache;

    const animations = this.outroSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    this._availableAnimationsCache = animations;
    return animations;
  }

  /**
   * Apply the "max_win" skin (GDD §18 — 10,000x celebration)
   */
  applySkin() {
    if (!this.outroSpine?.skeleton) return;

    try {
      const skinData = this.outroSpine.skeleton.data.findSkin("max_win");
      if (skinData) {
        this.outroSpine.skeleton.setSkin(skinData);
        this.outroSpine.skeleton.setSlotsToSetupPose();
      }
    } catch {
      // Silent fail — skin may not exist
    }
  }

  /**
   * Max text width in spine-local coords for font-size fitting.
   * Uses screen width only — bone width is too narrow and over-shrinks text.
   */
  _getScreenLocalMaxWidth(screenRatio = 0.85) {
    const spineScale = Math.abs(this.outroSpine?.scale?.x || 1);
    return spineScale > 0
      ? ((this.dimensions?.width || 800) * screenRatio) / spineScale
      : (this.dimensions?.width || 800) * screenRatio;
  }

  /**
   * Reduce font size so text fits within screen width (spine-local coords).
   */
  _fitFontSizeToWidth(text, baseStyle, baseFontSize, screenRatio = 0.85) {
    let fontSize = baseFontSize;
    const maxPixelWidth = this._getScreenLocalMaxWidth(screenRatio);
    const tempStyle = new PIXI.TextStyle(scaleStyle(baseStyle, fontSize));
    const metrics = PIXI.TextMetrics.measureText(text, tempStyle);
    if (metrics.width > maxPixelWidth && maxPixelWidth > 0) {
      fontSize = Math.max(
        10,
        Math.floor(fontSize * (maxPixelWidth / metrics.width)),
      );
    }
    return fontSize;
  }

  // ===================================================================
  //  COUNTER
  // ===================================================================

  initializeCounter() {
    if (!this.outroSpine || !this.outroSpine.skeleton) return;

    const counterSlot = this.outroSpine.skeleton.findSlot("txt_slot_value");
    if (!counterSlot) return;

    this.counterContainer = new PIXI.Container();
    this.counterContainer.name = "pc_max_win_counter_attached_to_slot";

    const counterBaseStyle = FONT_STYLES.winCelebrationCounter;
    this._counterBaseStyle = counterBaseStyle;
    this._counterBaseFontSize = counterBaseStyle.fontSize;

    this.counterText = new PIXI.Text(
      "0",
      new PIXI.TextStyle(scaleStyle(counterBaseStyle, this._counterBaseFontSize)),
    );
    this.counterText.anchor.set(0.5);
    this.counterContainer.addChild(this.counterText);

    this.outroSpine.addSlotObject("txt_slot_value", this.counterContainer);

    const bone = counterSlot.bone;
    this._counterBoneWidth = bone ? bone.data.length || 400 : 400;
  }

  updateCounterDisplay(value) {
    if (!this.counterText) return;

    const text = formatToCustomLocale(Math.floor(value));
    const fontSize = this._fitFontSizeToWidth(
      text,
      this._counterBaseStyle,
      this._counterBaseFontSize,
    );

    this.counterText.text = text;
    this.counterText.style = new PIXI.TextStyle(
      scaleStyle(this._counterBaseStyle, fontSize),
    );
    this.counterText.scale.set(1);
    if (this.counterContainer) {
      this.counterContainer.scale.set(1);
    }
  }

  // ===================================================================
  //  MULTIPLIER
  // ===================================================================

  initializeMultiplier() {
    if (!this.outroSpine || !this.outroSpine.skeleton) return;

    const multiplierSlot =
      this.outroSpine.skeleton.findSlot("txt_slot_multiplier");
    if (!multiplierSlot) return;

    this.multiplierContainer = new PIXI.Container();
    this.multiplierContainer.name = "pc_max_win_multiplier_attached_to_slot";

    this._multiplierBaseFontSize = Math.round(
      this._counterBaseStyle.fontSize * 0.6,
    );

    this.multiplierText = new PIXI.Text(
      "",
      new PIXI.TextStyle(
        scaleStyle(this._counterBaseStyle, this._multiplierBaseFontSize),
      ),
    );
    this.multiplierText.anchor.set(0.5);

    this.multiplierContainer.addChild(this.multiplierText);

    const bone = multiplierSlot.bone;
    this._multiplierBoneWidth = bone ? bone.data.length || 400 : 400;

    this.outroSpine.addSlotObject(
      "txt_slot_multiplier",
      this.multiplierContainer,
    );
  }

  updateMultiplierDisplay(winAmount) {
    if (!this.multiplierText || this._betAmount <= 0) return;

    const rawMultiplier = winAmount / this._betAmount;
    const multiplier = parseFloat(rawMultiplier.toFixed(2));
    if (multiplier <= 0) return;

    const betText = this.t("bet").toUpperCase();
    const text = `${multiplier.toLocaleString("en-US")}x ${betText}`;
    const fontSize = this._fitFontSizeToWidth(
      text,
      this._counterBaseStyle,
      this._multiplierBaseFontSize,
    );

    this.multiplierText.text = text;
    this.multiplierText.style = new PIXI.TextStyle(
      scaleStyle(this._counterBaseStyle, fontSize),
    );
    this.multiplierText.scale.set(1);
    if (this.multiplierContainer) {
      this.multiplierContainer.scale.set(1);
    }
  }

  // ===================================================================
  //  BUTTON
  // ===================================================================

  initializeButton() {
    if (!this.outroSpine || !this.outroSpine.skeleton) return;

    const tapSlot =
      this.outroSpine.skeleton.findSlot("txt_slot_tap_to_continue");
    if (!tapSlot) return;

    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "pc_max_win_continue_attached_to_slot";

    const buttonLabel = this.t("tap_to_continue").toUpperCase();
    const style = FONT_STYLES.introTapToStart;
    const btnFontSize = 42;

    this.buttonText = new PIXI.Text(
      buttonLabel,
      new PIXI.TextStyle(scaleStyle(style, btnFontSize)),
    );
    this.buttonText.anchor.set(0.5);

    this.buttonContainer.addChild(this.buttonText);

    const bone = tapSlot.bone;
    this._tapBoneWidth = bone ? bone.data.length || 400 : 400;
    if (this.buttonText.width > this._tapBoneWidth && this._tapBoneWidth > 0) {
      const scale = this._tapBoneWidth / this.buttonText.width;
      this.buttonContainer.scale.set(scale);
    }

    this.outroSpine.addSlotObject(
      "txt_slot_tap_to_continue",
      this.buttonContainer,
    );

    // Make the button clickable — triggers fullscreen + dismissal via component handler
    this.buttonContainer.eventMode = "static";
    this.buttonContainer.cursor = "pointer";
    this.buttonContainer.hitArea = new PIXI.Rectangle(
      -this._tapBoneWidth / 2, -40, this._tapBoneWidth, 80
    );
    this.buttonContainer.on("pointerdown", (evt) => {
      if (evt?.stopPropagation) evt.stopPropagation();
      if (this._buttonClickHandler) {
        this._buttonClickHandler(evt);
      }
    });

    this.applyButtonOffset();
  }

  applyButtonOffset() {
    // Spine overwrites container transform each frame — offset text inside container
    if (!this.buttonText) return;
    const config = this.getDeviceMultipliers();
    const yOffset = (config.buttonYOffset || 0) * (this.symbolHeight || 0);
    this.buttonText.y = yOffset;
  }

  // ===================================================================
  //  ANIMATION HELPERS
  // ===================================================================

  getSpineAnimationDuration(animationName) {
    if (!this.outroSpine?.skeleton?.data?.animations) return 1000;
    try {
      const animation = this.outroSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );
      if (animation && animation.duration) {
        return Math.round(animation.duration * 1000);
      }
      return 1000;
    } catch {
      return 1000;
    }
  }

  clearAllTimeouts() {
    if (this.skipProtectionTimeout) {
      this.skipProtectionTimeout.clear();
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      this.debounceTimeout.clear();
      this.debounceTimeout = null;
    }
    if (this.inIdleTimeout) {
      this.inIdleTimeout.clear();
      this.inIdleTimeout = null;
    }
    if (this.musicDelayTimeout) {
      this.musicDelayTimeout.clear();
      this.musicDelayTimeout = null;
    }
  }

  // ===================================================================
  //  ANIMATION SEQUENCE
  // ===================================================================

  setTotalWinValue(totalWin, bet) {
    this._totalWinValue = totalWin || 0;
    this._betAmount = bet || 0;
    this.updateCounterDisplay(this._totalWinValue);
    this.updateMultiplierDisplay(this._totalWinValue);
  }

  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.outroSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isWaitingForClick = false;
      this.hasHandledCloseClick = false;
      this.overlayRef.current?.fadeInBackground();

      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      this.clearAllTimeouts();

      // Fade out active feature music (background or ambient — they're exclusive)
      if (soundManager.isAmbientActive) {
        const featureAmbient = HowlCache[SOUND_NAME.feature_ambient_music];
        if (featureAmbient && featureAmbient.playing()) {
          soundManager.fadeOut(SOUND_NAME.feature_ambient_music, 500);
        }
      } else {
        const featureBG = HowlCache[SOUND_NAME.feature_background_music];
        if (featureBG && featureBG.playing()) {
          soundManager.fadeOut(SOUND_NAME.feature_background_music, 500);
        }
      }
      // Remember which was active so we can restore it
      this._wasAmbientActive = soundManager.isAmbientActive;

      // Play max win in sound + fade in loop music after BG fades out
      soundManager.play(SOUND_NAME.max_win_in);
      const musicDelay = new TickerTimeout(() => {
        const maxWinLoop = HowlCache[SOUND_NAME.max_win_loop];
        if (maxWinLoop) {
          maxWinLoop.loop(true);
          soundManager.fadeIn(SOUND_NAME.max_win_loop, 500);
        }
      }, 500);
      this.musicDelayTimeout = musicDelay;

      if (
        this.outroSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      this.outroSpine.state.clearTracks();
      this.outroSpine.state.clearListeners();
      this.outroSpine.state.apply(this.outroSpine.skeleton);
      this.outroSpine.skeleton.setToSetupPose();

      this.applySkin();

      // SKIP PROTECTION: block click for first second
      this.skipProtectionTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.isWaitingForClick = true;
          // GDD §16.4: Max Win — NO auto-close. Explicit user click only.
        }
      }, 1000);

      this.outroSpine.state.setAnimation(0, "in", false);

      if (this._showRAF) cancelAnimationFrame(this._showRAF);
      this._showRAF = requestAnimationFrame(() => {
        this._showRAF = null;
        if (this.isPlaying && this.outroSpine) {
          this.outroSpine.alpha = 1;
          this._startCoinShower();
        }
      });

      const inDuration = this.getSpineAnimationDuration("in");

      // Show total win partway through in animation
      const counterRevealTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.dispatch(setPotCollectionOutroCounterFinished(true));
        }
      }, Math.max(0, inDuration * 0.6));
      this._stopTimeouts = this._stopTimeouts || [];
      this._stopTimeouts.push(counterRevealTimeout);

      this.inIdleTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.outroSpine.state.setAnimation(0, "idle", true);
          soundManager.play(SOUND_NAME.max_win_idle);
          resolve();
        }
      }, inDuration);
    });
  }

  // ===================================================================
  //  USER INTERACTION
  // ===================================================================

  handleUserClick() {
    if (!this.isPlaying || !this.outroSpine?.state) return false;
    if (this.debounceTimeout) return false;
    if (!this.canSkipAnimation) return false;
    if (!this.isWaitingForClick) return false;

    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    this.hasHandledCloseClick = true;
    this.playClosingAnimation();
    return true;
  }

  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.outroSpine?.state) return;

    this.isWaitingForClick = false;

    this.outroSpine.state.clearListeners();

    // Play "out" animation and its one-shot sound
    this.outroSpine.state.setAnimation(0, "out", false);
    soundManager.play(SOUND_NAME.max_win_out);

    // Fade out max win loop over the full "out" animation duration
    // so the music plays through the entire closing sequence
    const outDuration = this.getSpineAnimationDuration("out");
    soundManager.fadeOut(SOUND_NAME.max_win_loop, outDuration);

    this.outroSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "out" && this.isPlaying) {
          // Restore whichever feature music was active before the dialog
          if (this._wasAmbientActive) {
            soundManager.fadeIn(SOUND_NAME.feature_ambient_music, 500);
          } else {
            soundManager.fadeIn(SOUND_NAME.feature_background_music, 500);
          }
          this.finishAnimation();
        }
      },
    });
  }

  finishAnimation() {
    if (!this.outroSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.outroSpine.alpha = 0.001;

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    soundManager.stop(SOUND_NAME.max_win_loop);

    this.clearAllTimeouts();

    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    // Close the max win dialog first (prevent re-trigger)
    this.dispatch(setShowPotCollectionMaxWin(false));

    // Trigger the same scene transition as total_win — back to base game
    this.dispatch(setPotCollectionOutroTransitionActive(true));

    this.overlayRef.current?.fadeOutBackground();

    this._stopCoinShower();
  }

  // ===================================================================
  //  COIN SHOWER
  // ===================================================================

  _startCoinShower() {
    if (!this.container || !FXManager.isReady) return;

    this._coinShowerContainer = new PIXI.Container();
    this._coinShowerContainer.name = "pc_max_win_coin_shower";
    this._coinShowerContainer.zIndex = 10;
    this._coinShowerContainer.y = 0;

    // Scale coin shower to 2× the spine's current scale — always proportional
    const spineScale = this.outroSpine?.scale.x || 1;
    this._coinShowerContainer.scale.set(spineScale * 2.5);

    this.container.addChild(this._coinShowerContainer);

    FXManager.startCoinShower(this._coinShowerContainer);
  }

  _stopCoinShower() {
    FXManager.stopCoinShower();

    if (this._coinShowerContainer) {
      if (this._coinShowerContainer.parent) {
        this._coinShowerContainer.parent.removeChild(this._coinShowerContainer);
      }
      if (!this._coinShowerContainer.destroyed) {
        this._coinShowerContainer.destroy({ children: true });
      }
      this._coinShowerContainer = null;
    }
  }

  // ===================================================================
  //  CLEANUP
  // ===================================================================

  cleanup() {
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    soundManager.stop(SOUND_NAME.max_win_loop);

    this._stopCoinShower();

    this.clearAllTimeouts();

    if (this.outroSpine?.removeSlotObjects) {
      this.outroSpine.removeSlotObjects();
    }

    if (this.counterContainer) {
      this.counterContainer.destroy({ children: true });
      this.counterContainer = null;
      this.counterText = null;
    }

    if (this.multiplierContainer) {
      this.multiplierContainer.destroy({ children: true });
      this.multiplierContainer = null;
      this.multiplierText = null;
    }

    if (this.buttonContainer) {
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
      this.buttonText = null;
    }

    if (this.outroSpine) {
      this.outroSpine.state?.clearListeners();
      this.outroSpine.state?.clearTracks();
      if (this.outroSpine.state) this.outroSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.outroSpine)) {
        this.container.removeChild(this.outroSpine);
      }
      this.outroSpine.destroy();
      this.outroSpine = null;
    }

    this.container = null;
    this.initialized = false;
  }
}

export default PotCollectionMaxWinDialogController;
